/**
 * Ledger > journal entry list (tracker item 31).
 *
 * Filters (startDate, endDate, sourceType, accountId) and the page live in the
 * query string, like RecurringListPage, so Chart of Accounts can link to
 * /ledger?accountId=… Route-gated to ADMIN / OFFICE_MANAGER (canViewLedger),
 * matching the backend.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createAPIClient, IGLAccountsApi, IJournalEntriesApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { dateParam, dateRangeError, pageParam } from './reports/reportUi';
import {
  LEDGER_NEW_PATH,
  LEDGER_PAGE_SIZE,
  createdByLabel,
  entryTotalCents,
  ledgerDate,
  ledgerEntryPath,
  ledgerMoney,
  reversalBadges,
  sourceTypeLabel,
  sourceTypeParam,
} from './ledgerUi';
import { Toast } from '../components/Toast';

export function ReversalBadges({ entry }: { entry: Pick<types.JournalEntry, 'reversedByEntryId' | 'reversesEntryId'> }) {
  return (
    <>
      {reversalBadges(entry).map((badge) => (
        <span
          key={badge.label}
          title={badge.title}
          className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${badge.className}`}
        >
          {badge.label}
        </span>
      ))}
    </>
  );
}

export function LedgerListPage() {
  const api = useMemo<IJournalEntriesApi & IGLAccountsApi>(() => createAPIClient(), []);
  const [params, setParams] = useSearchParams();
  const page = pageParam(params.get('page'));
  const startDate = dateParam(params.get('startDate'));
  const endDate = dateParam(params.get('endDate'));
  const sourceType = sourceTypeParam(params.get('sourceType'));
  const accountId = params.get('accountId') || '';
  const rangeError = dateRangeError(startDate, endDate);

  const [entries, setEntries] = useState<types.JournalEntry[]>([]);
  const [pagination, setPagination] = useState<types.PaginationInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accountName, setAccountName] = useState<string>('');

  useEffect(() => {
    if (rangeError) {
      setLoading(false);
      setEntries([]);
      setPagination(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    api.listJournalEntries(page, LEDGER_PAGE_SIZE, { startDate, endDate, sourceType, accountId })
      .then((result) => { if (active) { setEntries(result.data); setPagination(result.pagination); } })
      .catch((cause) => { if (active) setError(getErrorMessage(cause, 'Could not load journal entries')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [api, page, startDate, endDate, sourceType, accountId, rangeError]);

  // Name the account filter: from the rows when one of them posts to it, otherwise ask the API.
  const accountFromRows = useMemo(() => {
    if (!accountId) return '';
    for (const entry of entries) {
      const line = entry.lines.find((l) => l.glAccountId === accountId);
      if (line) return `${line.accountNumber} · ${line.accountName}`;
    }
    return '';
  }, [accountId, entries]);

  useEffect(() => {
    if (!accountId || accountFromRows || loading) return;
    let active = true;
    api.getGLAccount(accountId)
      .then((account) => { if (active) setAccountName(`${account.accountNumber} · ${account.accountName}`); })
      .catch(() => { if (active) setAccountName(''); });
    return () => { active = false; };
  }, [api, accountId, accountFromRows, loading]);

  function changeParams(next: Record<string, string>) {
    const updated = new URLSearchParams(params);
    Object.entries(next).forEach(([key, value]) => (value ? updated.set(key, value) : updated.delete(key)));
    setParams(updated);
  }

  const pages = Math.max(1, pagination?.pages || 1);
  const hasFilters = !!(startDate || endDate || sourceType || accountId);
  const accountChip = accountFromRows || accountName || accountId;

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
        <h1 className="text-2xl font-bold text-gray-900">Ledger</h1>
        <Link to={LEDGER_NEW_PATH} className={`${primaryButtonClass} inline-flex items-center min-h-[44px] sm:min-h-0`}>New manual entry</Link>
      </div>
      <p className="mb-6 max-w-3xl text-sm text-gray-600">
        Every posting to the general ledger. Entries cannot be edited or deleted; a manual adjustment is corrected by
        reversing it, and invoice or payment entries by cancelling the invoice or voiding the payment.
      </p>

      <section className="bg-white rounded-lg shadow p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-4 mb-5">
          <div className="w-full sm:w-auto">
            <label className={labelClass} htmlFor="ledger-start">From</label>
            <input id="ledger-start" type="date" className={inputClass} value={startDate} onChange={(event) => changeParams({ startDate: event.target.value, page: '' })} />
          </div>
          <div className="w-full sm:w-auto">
            <label className={labelClass} htmlFor="ledger-end">To</label>
            <input id="ledger-end" type="date" className={inputClass} value={endDate} onChange={(event) => changeParams({ endDate: event.target.value, page: '' })} />
          </div>
          <div className="w-full sm:max-w-xs">
            <label className={labelClass} htmlFor="ledger-source">Source</label>
            <select id="ledger-source" className={inputClass} value={sourceType} onChange={(event) => changeParams({ sourceType: event.target.value, page: '' })}>
              <option value="">All sources</option>
              {types.JOURNAL_SOURCE_TYPES.map((value) => <option key={value} value={value}>{sourceTypeLabel(value)}</option>)}
            </select>
          </div>
          {hasFilters && (
            <button type="button" className={`${secondaryButtonClass} w-full sm:w-auto min-h-[44px] sm:min-h-0`} onClick={() => changeParams({ startDate: '', endDate: '', sourceType: '', accountId: '', page: '' })}>
              Clear filters
            </button>
          )}
        </div>

        {accountId && (
          <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-gray-700">
            <span className="inline-flex max-w-full min-w-0 items-center gap-2 break-words rounded-full bg-blue-50 px-3 py-1 text-blue-800 ring-1 ring-inset ring-blue-600/20">
              Account: <strong>{accountChip}</strong>
              <button type="button" aria-label="Remove account filter" className="min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 font-bold text-blue-700 hover:text-blue-900" onClick={() => changeParams({ accountId: '', page: '' })}>
                ×
              </button>
            </span>
            <span className="text-gray-500">Entries with at least one line on this account (all lines shown).</span>
          </div>
        )}

        {rangeError && <Toast kind="error">{rangeError}</Toast>}
        {error && <Toast kind="error">{error}</Toast>}
        {rangeError ? null : loading ? <p>Loading journal entries…</p> : error ? null : (
          <>
            {entries.length === 0 ? (
              <p className="py-10 text-center text-gray-600">
                {hasFilters ? 'No journal entries match these filters.' : 'No journal entries yet.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="responsive-table min-w-full divide-y divide-gray-200 text-sm">
                  <caption className="sr-only">Journal entries</caption>
                  <thead className="bg-gray-50">
                    <tr>
                      {['Date', 'Memo', 'Source', 'Total', 'Created by'].map((heading) => (
                        <th key={heading} scope="col" className={`px-4 py-3 font-medium text-gray-600 ${heading === 'Total' ? 'text-right' : 'text-left'}`}>{heading}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {entries.map((entry) => (
                      <tr key={entry.id}>
                        <td data-label="Date" data-primary className="px-4 py-3 whitespace-nowrap">
                          <Link to={ledgerEntryPath(entry.id)} className="font-semibold text-blue-700 hover:underline">{ledgerDate(entry.entryDate)}</Link>
                        </td>
                        <td data-label="Memo" className="px-4 py-3">
                          <span className="min-w-0 break-words">
                            <Link to={ledgerEntryPath(entry.id)} className="text-gray-900 hover:underline">{entry.memo || <span className="text-gray-500">(no memo)</span>}</Link>
                            <ReversalBadges entry={entry} />
                          </span>
                        </td>
                        <td data-label="Source" className="px-4 py-3 whitespace-nowrap">{sourceTypeLabel(entry.sourceType)}</td>
                        <td data-label="Total" className="px-4 py-3 whitespace-nowrap text-right tabular-nums">{ledgerMoney(entryTotalCents(entry))}</td>
                        <td data-label="Created by" className="px-4 py-3 break-words">{createdByLabel(entry)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {pages > 1 && (
              <div className="flex flex-wrap justify-between items-center gap-3 mt-6">
                <button type="button" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={page <= 1} onClick={() => changeParams({ page: String(page - 1) })}>Previous</button>
                <span className="text-sm text-gray-600">Page {page} of {pages}</span>
                <button type="button" className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`} disabled={page >= pages} onClick={() => changeParams({ page: String(page + 1) })}>Next</button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
